import { sql, relations } from 'drizzle-orm';
import {
  check,
  index,
  int,
  sqliteTable,
  text,
  uniqueIndex,
  type AnySQLiteColumn,
} from 'drizzle-orm/sqlite-core';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod';
import type { JSONSchema7 } from 'json-schema';

export const organizationsTable = sqliteTable('organizations', {
  id: int().primaryKey({ autoIncrement: true }),
  slug: text().notNull().unique(),
  name: text().notNull(),
  description: text().notNull().default(''),
  createdAt: int()
    .notNull()
    .default(sql`(current_timestamp)`),
  updatedAt: int()
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const organizationsRelations = relations(
  organizationsTable,
  ({ many }) => ({
    events: many(eventTable, {
      relationName: 'organization',
    }),
  }),
);

// Better Auth core schema - User table
export const usersTable = sqliteTable('users', {
  id: int().primaryKey({ autoIncrement: true }),
  name: text(),
  email: text().notNull().unique(),
  emailVerified: int().default(0),
  stripeCustomerId: text().notNull(),
  image: text(),
  createdAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
  updatedAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
});

// Better Auth core schema - Session table
export const sessionTable = sqliteTable('sessions', {
  id: int().primaryKey(),
  userId: int()
    .notNull()
    .references(() => usersTable.id, { onDelete: 'cascade' }),
  token: text().notNull().unique(),
  expiresAt: int({ mode: 'timestamp' }).notNull(), // Unix timestamp
  ipAddress: text(), // Optional
  userAgent: text(), // Optional
  createdAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
  updatedAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
});

// Better Auth core schema - Account table
export const accountTable = sqliteTable('accounts', {
  id: int().primaryKey(),
  userId: int()
    .notNull()
    .references(() => usersTable.id, { onDelete: 'cascade' }),
  accountId: text().notNull(), // ID of the account as provided by SSO or equal to userId for credential accounts
  providerId: text().notNull(), // ID of the provider
  accessToken: text(), // Optional
  refreshToken: text(), // Optional
  accessTokenExpiresAt: int({ mode: 'timestamp' }), // Optional - Unix timestamp
  refreshTokenExpiresAt: int({ mode: 'timestamp' }), // Optional - Unix timestamp
  scope: text(), // Optional
  idToken: text(), // Optional
  password: text(), // Optional - mainly for email/password auth
  createdAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
  updatedAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
});

// Better Auth core schema - Verification table
export const verificationTable = sqliteTable('verifications', {
  id: int().primaryKey(),
  identifier: text().notNull(), // The identifier for the verification request
  value: text().notNull(), // The value to be verified
  expiresAt: int({ mode: 'timestamp' }).notNull(), // Unix timestamp when verification expires
  createdAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
  updatedAt: int({ mode: 'timestamp' })
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const usersRelations = relations(usersTable, ({ many }) => ({
  userProfiles: many(userProfileOwnersTable),
  sessions: many(sessionTable),
  accounts: many(accountTable),
}));

export const sessionRelations = relations(sessionTable, ({ one }) => ({
  user: one(usersTable, {
    fields: [sessionTable.userId],
    references: [usersTable.id],
  }),
}));

export const accountRelations = relations(accountTable, ({ one }) => ({
  user: one(usersTable, {
    fields: [accountTable.userId],
    references: [usersTable.id],
  }),
}));

export const organizationsUsersTable = sqliteTable(
  'organizations_users',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    userId: int()
      .notNull()
      .references(() => usersTable.id, { onDelete: 'cascade' }),
    role: text().$type<'admin'>().default('admin'),
  },
  (table) => [
    index('organizations_users_idx').on(table.organizationId, table.userId),
  ],
);

export const userProfilesTable = sqliteTable('user_profiles', {
  id: int().primaryKey({ autoIncrement: true }),
  firstName: text().notNull(),
  lastName: text().notNull(),
  createdAt: int()
    .notNull()
    .default(sql`(current_timestamp)`),
  updatedAt: int()
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const userProfilesRelations = relations(
  userProfilesTable,
  ({ many }) => ({
    userProfileOwners: many(userProfileOwnersTable),
  }),
);

export const userProfileOwnersTable = sqliteTable(
  'user_profile_owners',
  {
    id: int().primaryKey({ autoIncrement: true }),
    userId: int()
      .notNull()
      .references(() => usersTable.id, { onDelete: 'cascade' }),
    userProfileId: int()
      .notNull()
      .references(() => userProfilesTable.id, { onDelete: 'cascade' }),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('user_profile_owners_idx').on(table.userId, table.userProfileId),
  ],
);

export const userProfileOwnersRelations = relations(
  userProfileOwnersTable,
  ({ one }) => ({
    user: one(usersTable, {
      fields: [userProfileOwnersTable.userId],
      references: [usersTable.id],
    }),
    userProfile: one(userProfilesTable, {
      fields: [userProfileOwnersTable.userProfileId],
      references: [userProfilesTable.id],
    }),
  }),
);

export const userProfilesInsertSchema = createInsertSchema(
  userProfilesTable,
).extend({
  firstName: z
    .string()
    .trim()
    .min(2, 'First name must be at least 2 characters'),
  lastName: z.string().trim().min(2, 'Last name must be at least 2 characters'),
});

export const formsTable = sqliteTable(
  'forms',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    title: text().notNull(),
    description: text(),
    schema: text().notNull(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [index('forms_organization_idx').on(table.organizationId)],
);

export const formsInsertSchema = createInsertSchema(formsTable).extend({
  title: z.string().min(2, 'Title must be at least 2 characters'),
});

export const formsEntryTable = sqliteTable(
  'form_entries',
  {
    id: int().primaryKey({ autoIncrement: true }),
    userProfileId: int()
      .notNull()
      .references(() => userProfilesTable.id, { onDelete: 'cascade' }),
    formId: int()
      .notNull()
      .references(() => formsTable.id, { onDelete: 'cascade' }),
    fieldValues: text().notNull(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [index('forms_entry_idx').on(table.userProfileId, table.formId)],
);

export const eventTable = sqliteTable(
  'event',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    name: text().notNull(),
    shortDescription: text().notNull().default(''),
    description: text().notNull(),
    slug: text().notNull().unique(),
    startDate: int().notNull(),
    endDate: int().notNull(),
    publishDate: int(),
    lastRefundTimestamp: int(),
    cost: int(),
    multiDiscounts: text({ mode: 'json' }).$type<number[] | null>(),
    closed: text().$type<'manual' | null>(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('event_startDate_publishDate_idx').on(
      table.startDate,
      table.publishDate,
    ),
  ],
);

export const userProfileEventsTable = sqliteTable(
  'user_profile_events',
  {
    id: int().primaryKey({ autoIncrement: true }),
    userProfileId: int()
      .notNull()
      .references(() => userProfilesTable.id, { onDelete: 'cascade' }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    status: text()
      .notNull()
      .$type<
        'started' | 'complete' | 'paid' | 'cancelled' | 'confirmation_sent'
      >()
      .default('started'),
    checkoutSessionId: text(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('user_profile_events_idx').on(table.eventId, table.userProfileId),
  ],
);

export const userProfileEventsRelations = relations(
  userProfileEventsTable,
  ({ one }) => ({
    userProfile: one(userProfilesTable, {
      fields: [userProfileEventsTable.userProfileId],
      references: [userProfilesTable.id],
    }),
    event: one(eventTable, {
      fields: [userProfileEventsTable.eventId],
      references: [eventTable.id],
    }),
  }),
);

export const eventsRelations = relations(eventTable, ({ one, many }) => ({
  organization: one(organizationsTable, {
    fields: [eventTable.organizationId],
    references: [organizationsTable.id],
    relationName: 'organization',
  }),
  checkInOutConfigurations: many(checkInOutTable),
}));

export const eventsInsertSchema = createInsertSchema(eventTable).extend({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  description: z.string().min(2, 'Description must be at least 2 characters'),
  startDate: z.number().refine((date) => date > Date.now(), {
    message: 'Start date must be in the future',
  }),
  endDate: z.number().refine((date) => date > Date.now(), {
    message: 'End date must be in the future',
  }),
  publishDate: z.number().nullable().optional(),
});

export const eventFormsTable = sqliteTable(
  'event_forms',
  {
    id: int().primaryKey({ autoIncrement: true }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    formId: int()
      .notNull()
      .references(() => formsTable.id, { onDelete: 'cascade' }),
    formOrder: int().notNull(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('event_forms_idx').on(table.eventId, table.formOrder),
    index('event_forms_composite_idx').on(table.eventId, table.formId),
  ],
);

export const eventFormsRelations = relations(eventFormsTable, ({ one }) => ({
  event: one(eventTable, {
    fields: [eventFormsTable.eventId],
    references: [eventTable.id],
  }),
  form: one(formsTable, {
    fields: [eventFormsTable.formId],
    references: [formsTable.id],
  }),
  formSchema: one(formsTable, {
    fields: [eventFormsTable.formId],
    references: [formsTable.id],
  }),
}));

export const userEventAddonsTable = sqliteTable(
  'user_event_addons',
  {
    id: int().primaryKey({ autoIncrement: true }),
    userId: int()
      .notNull()
      .references(() => usersTable.id, { onDelete: 'cascade' }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    userProfileId: int()
      .notNull()
      .references(() => userProfilesTable.id, { onDelete: 'cascade' }),
    formSchemaId: int()
      .notNull()
      .references(() => formsTable.id, { onDelete: 'cascade' }),
    field: text().notNull(),
    // name and cost are going to be defined in the form schema
    name: text().notNull(),
    cost: int().notNull(),
    status: text()
      .$type<'unpaid' | 'switching' | 'paid' | 'cancelled'>()
      .default('unpaid'),
    checkoutSessionId: text(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [index('user_event_addons_idx').on(table.userId, table.eventId)],
);

export const eventPaymentLedgerTable = sqliteTable(
  'event_payment_ledger',
  {
    id: int().primaryKey({ autoIncrement: true }),
    idempotencyKey: text().notNull().unique(),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    eventId: int().notNull(),
    userProfileId: int()
      .notNull()
      .references(() => userProfilesTable.id, { onDelete: 'cascade' }),
    userProfileEventId: int().references(() => userProfileEventsTable.id, {
      onDelete: 'set null',
    }),
    userEventAddonId: int().references(() => userEventAddonsTable.id, {
      onDelete: 'set null',
    }),
    entryType: text().notNull().$type<'payment' | 'refund'>(),
    lineItemType: text().notNull().$type<'event' | 'addon'>(),
    amount: int().notNull(),
    currency: text().notNull(),
    receivedAt: int().notNull(),
    stripeCheckoutSessionId: text(),
    stripeInvoiceId: text(),
    stripeSubscriptionId: text(),
    stripePaymentIntentId: text(),
    stripeChargeId: text(),
    stripeRefundId: text(),
    stripeLineItemId: text(),
    stripeProductId: text(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('event_payment_ledger_event_idx').on(
      table.organizationId,
      table.eventId,
      table.receivedAt,
    ),
    index('event_payment_ledger_org_received_event_idx').on(
      table.organizationId,
      table.receivedAt,
      table.eventId,
    ),
    index('event_payment_ledger_user_profile_idx').on(
      table.userProfileId,
      table.receivedAt,
    ),
  ],
);

export const checkInOutLocationsTable = sqliteTable(
  'check_in_out_locations',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    name: text().notNull(),
    description: text().notNull().default(''),
    sortOrder: int().notNull().default(0),
    active: int().notNull().$type<0 | 1>().default(1),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('check_in_out_locations_event_idx').on(
      table.organizationId,
      table.eventId,
      table.sortOrder,
    ),
  ],
);

export type CheckInOutProminentField = {
  source: 'profile' | 'form_entry';
  formId: number | null;
  fieldKey: string;
};

export type CheckInOutFormConfiguration = {
  title: string;
  schema: JSONSchema7;
};

export const checkInOutTable = sqliteTable(
  'check_in_out',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    version: int().notNull().default(1),
    active: int().notNull().$type<0 | 1>().default(1),
    prominentFields: text({ mode: 'json' })
      .$type<CheckInOutProminentField[]>()
      .notNull()
      .default(sql`'[]'`),
    checkInForm: text({
      mode: 'json',
    }).$type<CheckInOutFormConfiguration | null>(),
    checkOutForm: text({
      mode: 'json',
    }).$type<CheckInOutFormConfiguration | null>(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('check_in_out_event_active_idx').on(
      table.organizationId,
      table.eventId,
      table.active,
    ),
    uniqueIndex('check_in_out_event_version_idx').on(
      table.eventId,
      table.version,
    ),
    uniqueIndex('check_in_out_event_active_unique_idx')
      .on(table.eventId)
      .where(sql`${table.active} = 1`),
  ],
);

export const checkInOutDeviceSetupAuthorizationsTable = sqliteTable(
  'check_in_out_device_setup_authorizations',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    kind: text().notNull().$type<'direct_setup' | 'shared_setup'>(),
    locationId: int().references(() => checkInOutLocationsTable.id, {
      onDelete: 'set null',
    }),
    tokenHash: text().notNull().unique(),
    createdByUserId: int().references(() => usersTable.id, {
      onDelete: 'set null',
    }),
    maxUses: int().notNull().default(1),
    useCount: int().notNull().default(0),
    expiresAt: int().notNull(),
    revokedAt: int(),
    lastUsedAt: int(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    check(
      'check_in_out_device_setup_authorizations_kind_check',
      sql`${table.kind} IN ('direct_setup', 'shared_setup')`,
    ),
    check(
      'check_in_out_device_setup_authorizations_usage_check',
      sql`${table.maxUses} > 0 AND ${table.useCount} >= 0 AND ${table.useCount} <= ${table.maxUses}`,
    ),
    check(
      'check_in_out_device_setup_authorizations_direct_check',
      sql`${table.kind} != 'direct_setup' OR (${table.locationId} IS NULL AND ${table.maxUses} = 1)`,
    ),
    index('check_in_out_device_setup_authorizations_event_idx').on(
      table.organizationId,
      table.eventId,
      table.kind,
      table.expiresAt,
    ),
  ],
);

export const checkInOutDevicesTable = sqliteTable(
  'check_in_out_devices',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    locationId: int().references(() => checkInOutLocationsTable.id, {
      onDelete: 'set null',
    }),
    setupAuthorizationId: int().references(
      () => checkInOutDeviceSetupAuthorizationsTable.id,
      { onDelete: 'set null' },
    ),
    name: text().notNull(),
    credentialHash: text().notNull().unique(),
    credentialPrefix: text().notNull(),
    provisionedByUserId: int().references(() => usersTable.id, {
      onDelete: 'set null',
    }),
    revokedAt: int(),
    lastSeenAt: int(),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
    updatedAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    index('check_in_out_devices_event_idx').on(
      table.organizationId,
      table.eventId,
      table.revokedAt,
    ),
  ],
);

export const checkInOutActionsTable = sqliteTable(
  'check_in_out_actions',
  {
    id: int().primaryKey({ autoIncrement: true }),
    organizationId: int()
      .notNull()
      .references(() => organizationsTable.id, { onDelete: 'cascade' }),
    eventId: int()
      .notNull()
      .references(() => eventTable.id, { onDelete: 'cascade' }),
    userProfileId: int()
      .notNull()
      .references(() => userProfilesTable.id, { onDelete: 'cascade' }),
    userProfileEventId: int().references(() => userProfileEventsTable.id, {
      onDelete: 'set null',
    }),
    // Historical action rows may outlive a deleted location. New sync actions
    // still require a location in the API and migration triggers.
    locationId: int().references(() => checkInOutLocationsTable.id, {
      onDelete: 'set null',
    }),
    deviceId: int()
      .notNull()
      .references(() => checkInOutDevicesTable.id, { onDelete: 'restrict' }),
    checkInOutId: int()
      .notNull()
      .references(() => checkInOutTable.id, { onDelete: 'restrict' }),
    operatorName: text().notNull(),
    clientActionId: text().notNull(),
    actionType: text().notNull().$type<'check_in' | 'check_out'>(),
    correctionOfActionId: int().references(
      (): AnySQLiteColumn => checkInOutActionsTable.id,
      { onDelete: 'restrict' },
    ),
    amendmentOfActionId: int().references(
      (): AnySQLiteColumn => checkInOutActionsTable.id,
      { onDelete: 'restrict' },
    ),
    occurredAt: int().notNull(),
    receivedAt: int().notNull(),
    note: text(),
    formAnswers: text({ mode: 'json' })
      .$type<
        Array<{
          fieldKey: string;
          fieldValue: unknown;
        }>
      >()
      .notNull()
      .default(sql`'[]'`),
    createdAt: int()
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => [
    uniqueIndex('check_in_out_actions_device_client_idx').on(
      table.deviceId,
      table.clientActionId,
    ),
    uniqueIndex('check_in_out_actions_correction_target_idx')
      .on(table.correctionOfActionId)
      .where(sql`${table.correctionOfActionId} IS NOT NULL`),
    index('check_in_out_actions_event_cursor_idx').on(
      table.organizationId,
      table.eventId,
      table.id,
    ),
    index('check_in_out_actions_amendment_target_idx')
      .on(
        table.amendmentOfActionId,
        table.occurredAt,
        table.receivedAt,
        table.id,
      )
      .where(sql`${table.amendmentOfActionId} IS NOT NULL`),
    index('check_in_out_actions_registration_idx').on(
      table.eventId,
      table.userProfileId,
      table.occurredAt,
    ),
    index('check_in_out_actions_export_idx').on(
      table.organizationId,
      table.eventId,
      table.occurredAt,
      table.receivedAt,
      table.id,
    ),
  ],
);

export const checkInOutLocationsRelations = relations(
  checkInOutLocationsTable,
  ({ one, many }) => ({
    organization: one(organizationsTable, {
      fields: [checkInOutLocationsTable.organizationId],
      references: [organizationsTable.id],
    }),
    event: one(eventTable, {
      fields: [checkInOutLocationsTable.eventId],
      references: [eventTable.id],
    }),
    devices: many(checkInOutDevicesTable),
    actions: many(checkInOutActionsTable),
  }),
);

export const checkInOutRelations = relations(
  checkInOutTable,
  ({ one, many }) => ({
    organization: one(organizationsTable, {
      fields: [checkInOutTable.organizationId],
      references: [organizationsTable.id],
    }),
    event: one(eventTable, {
      fields: [checkInOutTable.eventId],
      references: [eventTable.id],
    }),
    actions: many(checkInOutActionsTable),
  }),
);

export const checkInOutDeviceSetupAuthorizationsRelations = relations(
  checkInOutDeviceSetupAuthorizationsTable,
  ({ one, many }) => ({
    organization: one(organizationsTable, {
      fields: [checkInOutDeviceSetupAuthorizationsTable.organizationId],
      references: [organizationsTable.id],
    }),
    event: one(eventTable, {
      fields: [checkInOutDeviceSetupAuthorizationsTable.eventId],
      references: [eventTable.id],
    }),
    location: one(checkInOutLocationsTable, {
      fields: [checkInOutDeviceSetupAuthorizationsTable.locationId],
      references: [checkInOutLocationsTable.id],
    }),
    createdByUser: one(usersTable, {
      fields: [checkInOutDeviceSetupAuthorizationsTable.createdByUserId],
      references: [usersTable.id],
    }),
    devices: many(checkInOutDevicesTable),
  }),
);

export const checkInOutDevicesRelations = relations(
  checkInOutDevicesTable,
  ({ one, many }) => ({
    organization: one(organizationsTable, {
      fields: [checkInOutDevicesTable.organizationId],
      references: [organizationsTable.id],
    }),
    event: one(eventTable, {
      fields: [checkInOutDevicesTable.eventId],
      references: [eventTable.id],
    }),
    location: one(checkInOutLocationsTable, {
      fields: [checkInOutDevicesTable.locationId],
      references: [checkInOutLocationsTable.id],
    }),
    setupAuthorization: one(checkInOutDeviceSetupAuthorizationsTable, {
      fields: [checkInOutDevicesTable.setupAuthorizationId],
      references: [checkInOutDeviceSetupAuthorizationsTable.id],
    }),
    provisionedByUser: one(usersTable, {
      fields: [checkInOutDevicesTable.provisionedByUserId],
      references: [usersTable.id],
    }),
    actions: many(checkInOutActionsTable),
  }),
);

export const checkInOutActionsRelations = relations(
  checkInOutActionsTable,
  ({ one, many }) => ({
    organization: one(organizationsTable, {
      fields: [checkInOutActionsTable.organizationId],
      references: [organizationsTable.id],
    }),
    event: one(eventTable, {
      fields: [checkInOutActionsTable.eventId],
      references: [eventTable.id],
    }),
    userProfile: one(userProfilesTable, {
      fields: [checkInOutActionsTable.userProfileId],
      references: [userProfilesTable.id],
    }),
    userProfileEvent: one(userProfileEventsTable, {
      fields: [checkInOutActionsTable.userProfileEventId],
      references: [userProfileEventsTable.id],
    }),
    location: one(checkInOutLocationsTable, {
      fields: [checkInOutActionsTable.locationId],
      references: [checkInOutLocationsTable.id],
    }),
    device: one(checkInOutDevicesTable, {
      fields: [checkInOutActionsTable.deviceId],
      references: [checkInOutDevicesTable.id],
    }),
    checkInOut: one(checkInOutTable, {
      fields: [checkInOutActionsTable.checkInOutId],
      references: [checkInOutTable.id],
    }),
    correctionOfAction: one(checkInOutActionsTable, {
      fields: [checkInOutActionsTable.correctionOfActionId],
      references: [checkInOutActionsTable.id],
      relationName: 'correction',
    }),
    corrections: many(checkInOutActionsTable, {
      relationName: 'correction',
    }),
    amendmentOfAction: one(checkInOutActionsTable, {
      fields: [checkInOutActionsTable.amendmentOfActionId],
      references: [checkInOutActionsTable.id],
      relationName: 'amendment',
    }),
    amendments: many(checkInOutActionsTable, {
      relationName: 'amendment',
    }),
  }),
);
